import { produce } from 'immer';
import { defaults, IOptions } from 'sanitize-html';

import { SanitizationMergeProfile } from './mergeSanitizationProfiles.types';

/**
 * Merges two sanitization profiles.
 *
 * @param profile The native profile from the sanitization library.
 * @param other The other profile to merge with. This only includes a subset of
 *   the profile fields for simplicity, however, more can be implemented in the
 *   future.
 */
export function mergeSanitizationProfiles(
  profile: IOptions,
  other: SanitizationMergeProfile,
): IOptions {
  const { allowedClasses } = profile;
  const {
    allowedTags: otherAllowedTags = [],
    allowedClasses: otherAllowedClasses = {},
    allowedSchemes: otherAllowedSchemes = [],
  } = other;

  return produce(profile, (draft) => {
    if (otherAllowedTags.length) {
      if (typeof draft.allowedTags === 'undefined') {
        draft.allowedTags = Array.from(
          new Set(defaults.allowedTags.concat(otherAllowedTags)),
        );
      } else if (Array.isArray(draft.allowedTags)) {
        draft.allowedTags = Array.from(
          new Set(draft.allowedTags.concat(otherAllowedTags)),
        );
      }
    }

    if (other.allowedClasses) {
      if (typeof allowedClasses === 'undefined') {
        draft.allowedClasses = other.allowedClasses;
      } else {
        draft.allowedClasses = Object.keys(allowedClasses).reduce<
          IOptions['allowedClasses']
        >(
          (acc, tag) => ({
            ...acc,
            [tag]: [
              ...(allowedClasses[tag] as (string | RegExp)[]),
              ...(Array.isArray(otherAllowedClasses[tag])
                ? otherAllowedClasses[tag]
                : []),
            ],
          }),
          otherAllowedClasses,
        );
      }
    }

    if (otherAllowedSchemes.length) {
      if (typeof draft.allowedSchemes === 'undefined') {
        draft.allowedSchemes =
          defaults.allowedSchemes.concat(otherAllowedSchemes);
      } else if (Array.isArray(draft.allowedSchemes)) {
        draft.allowedSchemes = draft.allowedSchemes.concat(otherAllowedSchemes);
      }
    }
  });
}
